import type { TFunction } from 'i18next';
import type { ReactElement } from 'react';
import type { Quantity } from '~/graphql/graphql';
import {
  getPlatformProductDimensions,
  type ProductDimensionsInches,
} from './order';
import { quantityInUnit, stockPipelineInUnit } from './targetOrderSize';

type OptionalQuantity = Quantity | null | undefined;

export const UNKNOWN_PRODUCT_DIMENSIONS: ProductDimensionsInches = {
  lengthInches: 0,
  widthInches: 0,
  thicknessInches: 0,
};

export type WeeksCoverInput = {
  currentStock: OptionalQuantity;
  onOrderStock?: OptionalQuantity;
  inboundStock?: OptionalQuantity;
  usageRatePerWeek: OptionalQuantity;
  dimensions: ProductDimensionsInches;
};

/**
 * Weeks of usage covered by the stock pipeline, sharing `stockPipelineInUnit`
 * with the target order size so both read the same quantity. Infinity means "no
 * meaningful cover figure": no usage rate, or a unit the product dimensions
 * cannot reconcile.
 *
 * @example
 * weeksCover({
 *   currentStock: { value: 50, unit: 'PCS' },
 *   onOrderStock: { value: 5, unit: 'PCS' },
 *   inboundStock: { value: 5, unit: 'PCS' },
 *   usageRatePerWeek: { value: 12, unit: 'PCS' },
 *   dimensions: { lengthInches: 96, widthInches: 3.5, thicknessInches: 1.5 },
 * });
 */
export function weeksCover({
  currentStock,
  onOrderStock,
  inboundStock,
  usageRatePerWeek,
  dimensions,
}: WeeksCoverInput): number {
  const stockUnit = currentStock?.unit ?? usageRatePerWeek?.unit;
  if (!stockUnit) return Infinity;

  const convertedUsageRate = quantityInUnit(
    usageRatePerWeek,
    stockUnit,
    dimensions
  );
  if (convertedUsageRate === null || convertedUsageRate <= 0) return Infinity;

  const pipeline = stockPipelineInUnit(
    { currentStock, onOrderStock, inboundStock },
    stockUnit,
    dimensions
  );
  if (pipeline === null) return Infinity;

  return pipeline / convertedUsageRate;
}

type InventoryForWeeksCover = {
  quantity: Quantity;
  onOrderStock: Quantity;
  inboundStock: Quantity;
  buyerInventoryConfiguration?: {
    usageRatePerWeek: Quantity;
  } | null;
  platformProductsLength?: {
    isNominal?: boolean | null;
    length?: {
      nominalLengthInches?: number | null;
      actualLengthInches?: number | null;
    } | null;
    platformProduct?: {
      nominalWidthInches?: number | null;
      actualWidthInches?: number | null;
      nominalThicknessInches?: number | null;
      actualThicknessInches?: number | null;
      actualLengthInches?: number | null;
    } | null;
  } | null;
};

/**
 * Weeks of cover for one inventory row. Rows whose payload omits the product and
 * length — the priority-group sub-rows, which carry them on the group instead —
 * must pass `dimensions` explicitly.
 */
export function weeksCoverForInventory(
  inventory: InventoryForWeeksCover,
  dimensions?: ProductDimensionsInches
): number {
  return weeksCover({
    currentStock: inventory.quantity,
    onOrderStock: inventory.onOrderStock,
    inboundStock: inventory.inboundStock,
    usageRatePerWeek: inventory.buyerInventoryConfiguration?.usageRatePerWeek,
    dimensions:
      dimensions ??
      getPlatformProductDimensions(
        inventory.platformProductsLength?.platformProduct,
        inventory.platformProductsLength?.length,
        inventory.platformProductsLength?.isNominal
      ),
  });
}

export function statusFromWeeksCover(
  weeksCovered: number,
  weeksCoveredBeforeUrgent: number,
  weeksCoveredBeforeMonitor: number,
  t: (key: string) => string
): ReactElement {
  if (weeksCovered <= weeksCoveredBeforeUrgent) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium text-error-700 bg-error-100">
        {t('inventory.urgent')}
      </span>
    );
  }

  if (weeksCovered <= weeksCoveredBeforeMonitor) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium text-warning-700 bg-warning-100">
        {t('inventory.soon')}
      </span>
    );
  }

  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
      {t('inventory.healthy')}
    </span>
  );
}

export const isNonNegativeNumber = (v: number | ''): boolean =>
  v !== '' && !Number.isNaN(v) && v >= 0;

export function getStockHealthOptions(
  t: TFunction<'inventory'>
): { label: string; value: 'HEALTHY' | 'LOW' | 'CRITICAL' }[] {
  return [
    { label: t('inventory.filter.status_values.healthy'), value: 'HEALTHY' },
    { label: t('inventory.filter.status_values.low'), value: 'LOW' },
    { label: t('inventory.filter.status_values.critical'), value: 'CRITICAL' },
  ];
}
